// src/components/setting/SettingsPage.js - 支払い管理・支払い設定タブ対応
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, update, onValue, get } from 'firebase/database';
import { mainDatabase, contentUsersDatabase } from '../../firebase';
import PaymentRequestList from '../admin/PaymentRequestList';

import shared from '../../styles/shared.module.css';
import styles from './Settings.module.css';
import adminStyles from '../admin/Admin.module.css';

// 当日の日付判定（モジュールレベルで安定した参照）
const getTodayDateString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};
const isToday = (dateString) => {
  if (!dateString) return false;
  return dateString.startsWith(getTodayDateString());
};

const SettingsPage = () => {
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [activeTab, setActiveTab] = useState('paymentManagement'); // 'paymentManagement' | 'paymentSettings'
  const [paymentSettings, setPaymentSettings] = useState({
    paylink: '',
    payvalue: 1000,
    description: 'フリードリンク料金',
    closingTime: '22:00'
  });
  // 支払い管理タブ用
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [processedPayments, setProcessedPayments] = useState([]);

  // 支払いリクエスト表示用の名前（本名を優先、なければニックネーム・メール・ゲスト）
  const getPaymentRequestDisplayName = (userData) => {
    const lastName = (userData.lastName || '').trim();
    const firstName = (userData.firstName || '').trim();
    const fullName = [lastName, firstName].filter(Boolean).join(' ');
    if (fullName) return fullName;
    return userData.nickname || userData.email || 'ゲスト';
  };

  // 支払いリクエストを取得
  const fetchPaymentRequestsDirectly = useCallback(async () => {
    try {
      const db = getDatabase();
      const usersRef = ref(db, 'users');
      const snapshot = await get(usersRef);
      const usersData = snapshot.val();
      if (!usersData) {
        setPaymentRequests([]);
        setProcessedPayments([]);
        return;
      }
      const pendingRequests = [];
      const processedUsers = [];
      for (const userId in usersData) {
        const userData = usersData[userId];
        if (userData.paymentStatus && userData.paymentStatus.toLowerCase() === 'pending') {
          pendingRequests.push({
            userId,
            ...userData,
            userDisplayName: getPaymentRequestDisplayName(userData)
          });
        } else if (userData.paymentStatus &&
          ((userData.paymentCompletedAt && isToday(userData.paymentCompletedAt)) ||
            (userData.paymentRejectedAt && isToday(userData.paymentRejectedAt)))) {
          processedUsers.push({
            userId,
            ...userData,
            userDisplayName: getPaymentRequestDisplayName(userData),
            processType: userData.paymentStatus.toLowerCase() === 'completed' ? 'approved' : 'rejected',
            processedAt: userData.paymentStatus.toLowerCase() === 'completed'
              ? userData.paymentCompletedAt : userData.paymentRejectedAt
          });
        }
      }
      const sortedRequests = pendingRequests.sort((a, b) => {
        const dateA = a.paymentRequestedAt ? new Date(a.paymentRequestedAt) : new Date(0);
        const dateB = b.paymentRequestedAt ? new Date(b.paymentRequestedAt) : new Date(0);
        return dateB - dateA;
      });
      const sortedProcessed = processedUsers.sort((a, b) => {
        const dateA = a.processedAt ? new Date(a.processedAt) : new Date(0);
        const dateB = b.processedAt ? new Date(b.processedAt) : new Date(0);
        return dateB - dateA;
      });
      setPaymentRequests(sortedRequests);
      setProcessedPayments(sortedProcessed);
    } catch (err) {
      console.error('支払いリクエストの取得エラー:', err);
      setError('支払いリクエストの取得中にエラーが発生しました。');
    }
  // isToday はモジュールレベルの安定した関数のため、依存配列から除外
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 管理者権限チェック
  useEffect(() => {
    const checkAdmin = async () => {
      if (currentUser) {
        try {
          const adminStatus = await isAdmin(currentUser.uid);
          setIsAdminUser(adminStatus);
          if (!adminStatus) {
            navigate('/dashboard');
          }
        } catch (err) {
          console.error('管理者権限チェックエラー:', err);
          navigate('/dashboard');
        }
      }
    };
    checkAdmin();
  }, [currentUser, isAdmin, navigate]);

  // 支払い設定を取得
  useEffect(() => {
    const db = contentUsersDatabase; // 新しいDBから設定を取得
    const settingsRef = ref(db, 'settings/payment');

    const unsubscribe = onValue(settingsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setPaymentSettings({
          paylink: data.paylink || '',
          payvalue: data.payvalue !== undefined ? data.payvalue : 1000,
          closingTime: data.closingTime || '22:00'
        });
      }
      setLoading(false);
    }, (error) => {
      console.error('設定の取得エラー:', error);

      // 新DBで失敗した場合、旧DBから試行
      const oldSettingsRef = ref(mainDatabase, 'settings/payment');
      onValue(oldSettingsRef, (snapshot) => {
        const oldData = snapshot.val();
        if (oldData) {
          setPaymentSettings({
            paylink: oldData.paylink || '',
            payvalue: oldData.payvalue !== undefined ? oldData.payvalue : 1000,
            closingTime: oldData.closingTime || '22:00'
          });
        }
        setLoading(false);
      }, (oldError) => {
        setError('設定情報の取得に失敗しました。');
        setLoading(false);
      });
    });

    return () => unsubscribe();
  }, []);

  // 支払い設定を保存
  const handleSaveSettings = async (e) => {
    e.preventDefault();

    if (!isAdminUser) {
      setError('この操作を行うには管理者権限が必要です。');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // 両方のDBに設定を保存
      const newDb = contentUsersDatabase;
      const oldDb = mainDatabase;

      const settingsRef = ref(newDb, 'settings/payment');
      const oldSettingsRef = ref(oldDb, 'settings/payment');

      // 数値変換を確実に行う
      const payvalue = parseInt(paymentSettings.payvalue, 10);

      // NaNチェック（入力が空または数値でない場合）
      if (isNaN(payvalue) && paymentSettings.payvalue !== 0) {
        throw new Error('支払い金額は有効な数値である必要があります。');
      }

      const updatedSettings = {
        ...paymentSettings,
        payvalue: payvalue, // 0も正しく保存される
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid
      };

      // 新DBに保存
      await update(settingsRef, updatedSettings);

      // 互換性のために旧DBにも保存
      await update(oldSettingsRef, updatedSettings);

      setSuccess('設定が正常に保存されました。');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('設定保存エラー:', error);
      setError(`設定の保存に失敗しました: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // 支払い情報をリセット（すべてのユーザーを未支払いに戻す）
  const handleResetPaymentStatus = async () => {
    if (!isAdminUser) {
      setError('この操作を行うには管理者権限が必要です。');
      return;
    }

    // 確認ダイアログ
    const confirmed = window.confirm(
      '本当にすべてのユーザーの支払い情報をリセットしますか？\nこの操作は元に戻せません。'
    );

    if (!confirmed) {
      return;
    }

    setIsResetting(true);
    setError('');
    setSuccess('');

    try {
      const db = getDatabase();
      const usersRef = ref(db, 'users');

      // すべてのユーザーデータを取得
      const snapshot = await get(usersRef);
      const usersData = snapshot.val();

      if (!usersData) {
        throw new Error('ユーザーデータが見つかりません。');
      }

      const resetPayload = {
        paymentStatus: 'unpaid',
        paymentRequestedAt: '',
        paymentCompletedAt: '',
        paymentAmount: 0
      };

      const paymentInfoResetPayload = {
        currentStatus: 'unpaid',
        requestedAt: '',
        completedAt: '',
        rejectedAt: '',
        amount: 0
      };

      // 各ユーザーの支払い情報を個別にリセット（main DB + content DB の両方）
      const resetPromises = [];

      for (const userId in usersData) {
        if (usersData[userId].paymentStatus !== 'unpaid') {
          // 1) Main DB（デフォルトDB）をリセット
          const userRef = ref(db, `users/${userId}`);
          resetPromises.push(
            update(userRef, resetPayload)
              .then(() => true)
              .catch((err) => {
                console.error(`Main DB: ユーザー ${userId} の更新エラー:`, err);
                return false;
              })
          );
        }

        // 2) Content DB の users/profiles/{userId}/paymentInfo をリセット（プロフィールが存在する場合のみ）
        const contentProfileRef = ref(contentUsersDatabase, `users/profiles/${userId}`);
        resetPromises.push(
          get(contentProfileRef)
            .then((profileSnapshot) => {
              if (profileSnapshot.exists()) {
                return update(contentProfileRef, {
                  paymentInfo: paymentInfoResetPayload,
                  updatedAt: new Date().toISOString()
                }).then(() => true).catch((err) => {
                  console.error(`Content DB: ユーザー ${userId} の更新エラー:`, err);
                  return false;
                });
              }
              return true;
            })
            .catch((err) => {
              console.error(`Content DB: ユーザー ${userId} のプロフィール取得エラー:`, err);
              return false;
            })
        );
      }

      await Promise.all(resetPromises);
      // リセットしたユーザー数は main DB で unpaid でなかった人数とする（表示メッセージ用）
      let mainResetCount = 0;
      for (const userId in usersData) {
        if (usersData[userId].paymentStatus !== 'unpaid') mainResetCount++;
      }

      // 変更があった場合のみログを記録（ログ書き込みは権限で失敗することがあるため失敗しても続行）
      if (mainResetCount > 0) {
        const logRef = ref(db, `settings/logs/payment_reset_${Date.now()}`);
        try {
          await update(logRef, {
            action: 'payment_reset',
            executedBy: currentUser.uid,
            executedAt: new Date().toISOString(),
            affectedUsers: mainResetCount
          });
        } catch (logErr) {
          console.warn('支払いリセットのログ記録に失敗しました（リセット処理は完了しています）:', logErr);
        }

        setSuccess(`${mainResetCount}人のユーザーの支払い情報をリセットしました。（Main DB と Content DB の両方を更新）`);
      } else {
        setSuccess('リセットが必要なユーザーはいませんでした。');
      }

      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('支払い情報リセットエラー:', error);
      setError(`支払い情報のリセット中にエラーが発生しました: ${error.message}`);
    } finally {
      setIsResetting(false);
    }
  };

  // 支払いリクエストをリアルタイム購読
  useEffect(() => {
    const db = getDatabase();
    const usersRef = ref(db, 'users');
    const unsubscribe = onValue(usersRef, (snapshot) => {
      try {
        const usersData = snapshot.val();
        if (usersData) {
          const pendingRequests = [];
          const processedUsers = [];
          for (const userId in usersData) {
            const userData = usersData[userId];
            if (userData.paymentStatus && userData.paymentStatus.toLowerCase() === 'pending') {
              pendingRequests.push({
                userId,
                ...userData,
                userDisplayName: getPaymentRequestDisplayName(userData)
              });
            } else if (userData.paymentStatus &&
              ((userData.paymentCompletedAt && isToday(userData.paymentCompletedAt)) ||
                (userData.paymentRejectedAt && isToday(userData.paymentRejectedAt)))) {
              processedUsers.push({
                userId,
                ...userData,
                userDisplayName: getPaymentRequestDisplayName(userData),
                processType: userData.paymentStatus.toLowerCase() === 'completed' ? 'approved' : 'rejected',
                processedAt: userData.paymentStatus.toLowerCase() === 'completed'
                  ? userData.paymentCompletedAt : userData.paymentRejectedAt
              });
            }
          }
          const sortedRequests = pendingRequests.sort((a, b) => {
            const dateA = a.paymentRequestedAt ? new Date(a.paymentRequestedAt) : new Date(0);
            const dateB = b.paymentRequestedAt ? new Date(b.paymentRequestedAt) : new Date(0);
            return dateB - dateA;
          });
          const sortedProcessed = processedUsers.sort((a, b) => {
            const dateA = a.processedAt ? new Date(a.processedAt) : new Date(0);
            const dateB = b.processedAt ? new Date(b.processedAt) : new Date(0);
            return dateB - dateA;
          });
          setPaymentRequests(sortedRequests);
          setProcessedPayments(sortedProcessed);
        } else {
          setPaymentRequests([]);
          setProcessedPayments([]);
        }
      } catch (err) {
        console.error('支払いリクエストの取得エラー:', err);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    fetchPaymentRequestsDirectly();
  }, [fetchPaymentRequestsDirectly]);

  // 支払いリクエストを承認
  const handleApprovePayment = async (userId) => {
    try {
      const mainDb = getDatabase();
      const mainUserRef = ref(mainDb, `users/${userId}`);
      await update(mainUserRef, {
        paymentStatus: 'completed',
        paymentCompletedAt: new Date().toISOString()
      });
      try {
        const newDb = contentUsersDatabase;
        const newUserRef = ref(newDb, `users/profiles/${userId}`);
        const snapshot = await get(newUserRef);
        const userData = snapshot.val() || {};
        const paymentInfo = userData.paymentInfo || {};
        paymentInfo.currentStatus = 'completed';
        paymentInfo.completedAt = new Date().toISOString();
        await update(newUserRef, {
          paymentInfo,
          updatedAt: new Date().toISOString()
        });
      } catch (newDbError) {
        console.error('新DB更新エラー:', newDbError);
      }
      fetchPaymentRequestsDirectly();
    } catch (err) {
      console.error('支払い承認エラー:', err);
      setError('支払い承認処理中にエラーが発生しました。');
    }
  };

  // 支払いリクエストを拒否
  const handleRejectPayment = async (userId) => {
    try {
      const mainDb = getDatabase();
      const mainUserRef = ref(mainDb, `users/${userId}`);
      await update(mainUserRef, {
        paymentStatus: 'unpaid',
        paymentRejectedAt: new Date().toISOString()
      });
      try {
        const newDb = contentUsersDatabase;
        const newUserRef = ref(newDb, `users/profiles/${userId}`);
        const snapshot = await get(newUserRef);
        const userData = snapshot.val() || {};
        const paymentInfo = userData.paymentInfo || {};
        paymentInfo.currentStatus = 'unpaid';
        paymentInfo.rejectedAt = new Date().toISOString();
        await update(newUserRef, {
          paymentInfo,
          updatedAt: new Date().toISOString()
        });
      } catch (newDbError) {
        console.error('新DB更新エラー:', newDbError);
      }
      fetchPaymentRequestsDirectly();
    } catch (err) {
      console.error('支払い拒否エラー:', err);
      setError('支払い拒否処理中にエラーが発生しました。');
    }
  };

  // 入力変更ハンドラー
  const handleChange = (e) => {
    const { name, value } = e.target;

    // 支払い金額が数値型になるよう変換（空文字を含む）
    if (name === 'payvalue') {
      const numValue = value === '' ? 0 : parseInt(value, 10);
      setPaymentSettings(prev => ({
        ...prev,
        [name]: numValue
      }));
    } else {
      setPaymentSettings(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  if (loading) {
    return (
      <div className={shared.loadingSpinner}>
        <div className={shared.spinner}></div>
      </div>
    );
  }

  return (
    <div className={shared.pageContainerWithTabs}>
      <header className={shared.pageHeader}>
        <div className={shared.headerContainer}>
          <h1 className={shared.headerTitle}>BANNAI'S BAR - 支払い管理</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className={`${shared.backButton} ${styles.backButton}`}
          >
            戻る
          </button>
        </div>
      </header>

      <main className={shared.mainContentWithTabs}>
        {/* タブ: 支払い管理 / 支払い設定（固定） */}
        <div className={styles.tabContainer}>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === 'paymentManagement' ? styles.tabButtonActive : ''}`}
            onClick={() => { setActiveTab('paymentManagement'); fetchPaymentRequestsDirectly(); }}
          >
            支払い管理
            {paymentRequests.length > 0 && (
              <span className={adminStyles.badge}>{paymentRequests.length}</span>
            )}
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === 'paymentSettings' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('paymentSettings')}
          >
            支払い設定
          </button>
        </div>

        {/* タブの中身のみスクロール */}
        <div className={shared.tabContentScroll}>
          {error && (
            <div className={shared.errorMessage}>{error}</div>
          )}
          {success && (
            <div className={shared.successMessage}>{success}</div>
          )}

          {/* 支払い管理タブ */}
          {activeTab === 'paymentManagement' && (
          <>
            <div className={adminStyles.sectionHeader}>
              <h2 className={adminStyles.sectionTitle}>支払いリクエスト管理</h2>
              <p className={adminStyles.sectionSubtitle}>
                顧客からの支払いリクエストを承認または拒否します。
              </p>
            </div>
            <div className={adminStyles.paymentSection}>
              <h3 className={`${adminStyles.sectionHeading} ${adminStyles.pendingPaymentsHeading}`}>
                処理待ちの支払いリクエスト ({paymentRequests.length})
              </h3>
              {paymentRequests.length === 0 ? (
                <div className={adminStyles.emptyState}>
                  <p className={adminStyles.emptyStateText}>現在処理待ちの支払いリクエストはありません</p>
                </div>
              ) : (
                <PaymentRequestList
                  paymentRequests={paymentRequests}
                  onApprove={handleApprovePayment}
                  onReject={handleRejectPayment}
                  isPending={true}
                />
              )}
            </div>
            <div className={adminStyles.paymentSection} style={{ marginTop: '2rem' }}>
              <h3 className={`${adminStyles.sectionHeading} ${adminStyles.processedPaymentsHeading}`}>
                本日処理された支払い ({processedPayments.length})
              </h3>
              {processedPayments.length === 0 ? (
                <div className={adminStyles.emptyState}>
                  <p className={adminStyles.emptyStateText}>本日処理された支払いはありません</p>
                </div>
              ) : (
                <PaymentRequestList
                  paymentRequests={processedPayments}
                  isPending={false}
                  isProcessed={true}
                />
              )}
            </div>
            <div className={adminStyles.sectionControls} style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={fetchPaymentRequestsDirectly}
                className={adminStyles.reloadButton}
              >
                <svg className={adminStyles.reloadIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                データを更新
              </button>
            </div>
          </>
        )}

        {/* 支払い設定タブ: 支払い設定 + 支払い情報リセット */}
        {activeTab === 'paymentSettings' && (
          <>
            <form onSubmit={handleSaveSettings}>
              <div className={shared.card}>
                <h2 className={shared.cardTitle}>支払い設定</h2>
                <div className={shared.formGroupTight}>
                  <label className={shared.label}>支払い金額 (円)</label>
                  <input
                    type="number"
                    name="payvalue"
                    value={paymentSettings.payvalue}
                    onChange={handleChange}
                    className={shared.input}
                    min="0"
                    step="100"
                    required
                  />
                  <p className={shared.helperText}>0円に設定すると無料キャンペーンモードになります。</p>
                </div>
                <div className={shared.formGroupTight}>
                  <label className={shared.label}>PayPay支払いリンク</label>
                  <input
                    type="text"
                    name="paylink"
                    value={paymentSettings.paylink}
                    onChange={handleChange}
                    className={shared.input}
                    placeholder="https://paypal.me/yourusername"
                  />
                  <p className={shared.helperText}>PayPayでの支払いを使用しない場合は空にしてください。</p>
                </div>
                <div className={shared.formGroupTight}>
                  <label className={shared.label}>本日の閉店時間</label>
                  <input
                    type="time"
                    name="closingTime"
                    value={paymentSettings.closingTime}
                    onChange={handleChange}
                    className={shared.input}
                    required
                  />
                  <p className={shared.helperText}>店舗の閉店時間を24時間形式で入力してください（例：22:00）</p>
                </div>
                <div className={shared.buttonContainerEnd}>
                  <button type="submit" disabled={saving} className={shared.saveButton}>
                    {saving ? '保存中...' : '設定を保存'}
                  </button>
                </div>
              </div>
            </form>
            <div className={shared.card}>
              <h2 className={shared.cardTitle}>支払い情報リセット</h2>
              <div className={styles.dangerZone}>
                <h3 className={styles.dangerZoneTitle}>危険な操作</h3>
                <p className={styles.dangerZoneDescription}>
                  以下の操作は取り消しができません。十分に注意して実行してください。
                </p>
                <div className={styles.dangerAction}>
                  <div className={styles.dangerActionInfo}>
                    <h4 className={styles.dangerActionTitle}>支払い情報のリセット</h4>
                    <p className={styles.dangerActionDescription}>
                      すべてのユーザーの支払い情報を「未支払い」にリセットします。
                      イベント終了時や新規イベント開始前に実行してください。
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleResetPaymentStatus}
                    disabled={isResetting}
                    className={styles.dangerButton}
                  >
                    {isResetting ? 'リセット中...' : '支払い情報をリセット'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;